import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ApplicationStatus from './components/ApplicationStatus';
import DocumentManager from './components/DocumentManager';
import AccountOverview from './components/AccountOverview';
import MessagingInterface from './components/MessagingInterface';
import PerformanceMetrics from './components/PerformanceMetrics';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const PartnerPortalDashboard = () => {
  const { user, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [partnerData, setPartnerData] = useState(null);
  const [applicationData, setApplicationData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (user) {
      loadPartnerData();
    }
  }, [user]);

  const loadPartnerData = async () => {
    setLoading(true);
    try {
      // Load partner profile
      const { data: partner, error: partnerError } = await supabase
        ?.from('partners')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.single();

      if (partnerError && partnerError?.code !== 'PGRST116') {
        console.error('Error loading partner:', partnerError);
      }

      setPartnerData(partner);

      // Load documents
      const { data: docs, error: docsError } = await supabase
        ?.from('documents')
        ?.select('*')
        ?.eq('owner_id', user?.id)
        ?.eq('owner_type', 'partner')
        ?.order('uploaded_at', { ascending: false });

      if (docsError) {
        console.error('Error loading documents:', docsError);
      } else {
        setDocuments(docs || []);
      }

      // Load messages
      const { data: msgs, error: msgsError } = await supabase
        ?.from('messages')
        ?.select('*, collaboration_threads(title)')
        ?.eq('sender_id', user?.id)
        ?.order('sent_at', { ascending: false })
        ?.limit(10);

      if (msgsError) {
        console.error('Error loading messages:', msgsError);
      } else {
        setMessages(msgs || []);
      }
    } catch (error) {
      console.error('Error loading partner data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadPartnerData();
    setRefreshing(false);
  };

  const breadcrumbItems = [
    { label: 'Roamsure', path: '/partner-portal-dashboard' },
    { label: 'Partner Portal', path: '/partner-portal-dashboard' }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'application', label: 'Application Status', icon: 'FileText' },
    { id: 'documents', label: 'Documents', icon: 'FolderOpen' },
    { id: 'messages', label: 'Messages', icon: 'MessageSquare' },
    { id: 'performance', label: 'Performance', icon: 'TrendingUp' },
    { id: 'settings', label: 'Settings', icon: 'Settings' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20 px-6 pb-8">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading partner portal...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 px-6 pb-8">
        {/* Breadcrumb */}
        <div className="mb-6">
          <nav className="flex items-center space-x-2 text-sm" aria-label="Breadcrumb">
            {breadcrumbItems?.map((item, index) => (
              <React.Fragment key={item?.path}>
                {index > 0 && (
                  <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
                )}
                <span className={index === breadcrumbItems?.length - 1 ? 'text-foreground font-medium' : 'text-muted-foreground'}>
                  {item?.label}
                </span>
              </React.Fragment>
            ))}
          </nav>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Partner Portal Dashboard</h1>
            <p className="text-muted-foreground">
              Manage your partnership application, documents, and account settings
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              iconName="RefreshCw"
              onClick={handleRefresh}
              disabled={refreshing}
              loading={refreshing}
            >
              Refresh
            </Button>
            <Button iconName="HelpCircle" variant="ghost">
              Help Center
            </Button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="glass-container rounded-xl border border-border/50 p-2 mb-6">
          <div className="flex items-center space-x-2 overflow-x-auto">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 whitespace-nowrap ${
                  activeTab === tab?.id
                    ? 'bg-primary text-primary-foreground shadow-md'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={18} />
                <span className="font-medium">{tab?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <AccountOverview partnerData={partnerData} userProfile={userProfile} />
                <ApplicationStatus partnerData={partnerData} onRefresh={loadPartnerData} />
              </div>
              <div className="space-y-6">
                <MessagingInterface messages={messages} onRefresh={loadPartnerData} />
              </div>
            </div>
          )}

          {activeTab === 'application' && (
            <ApplicationStatus partnerData={partnerData} onRefresh={loadPartnerData} expanded />
          )}

          {activeTab === 'documents' && (
            <DocumentManager documents={documents} onRefresh={loadPartnerData} />
          )}

          {activeTab === 'messages' && (
            <MessagingInterface messages={messages} onRefresh={loadPartnerData} expanded />
          )}

          {activeTab === 'performance' && (
            <PerformanceMetrics partnerData={partnerData} />
          )}

          {activeTab === 'settings' && (
            <div className="glass-container rounded-xl border border-border/50 p-6">
              <h2 className="text-xl font-bold text-foreground mb-4">Account Settings</h2>
              <p className="text-muted-foreground mb-6">
                Manage your contact information, notification preferences, and user access controls.
              </p>
              <div className="space-y-4">
                <Button iconName="User" variant="outline" fullWidth>
                  Update Contact Information
                </Button>
                <Button iconName="Bell" variant="outline" fullWidth>
                  Notification Preferences
                </Button>
                <Button iconName="Users" variant="outline" fullWidth>
                  Manage Team Access
                </Button>
                <Button iconName="Lock" variant="outline" fullWidth>
                  Security Settings
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default PartnerPortalDashboard;